<template>
  <v-card :rounded="false" flat>
    <v-form ref="form" lazy-validation @submit.prevent="submit">
      <div>
        <v-text-field
          label="Email"
          v-model="email"
          :rules="loginEmailRules"
          required
          :error="errors.hasOwnProperty('email')"
          :messages="errors.email"
          :disabled="loading"
          type="email"
        ></v-text-field>
      </div>
      <div class="aa-icons-eye">
        <v-text-field
          label="Password"
          v-model="password"
          :append-icon="show1 ? 'mdi-eye-off' : 'mdi-eye'"
          :rules="[rules.required, rules.min]"
          :type="show1 ? 'text' : 'password'"
          name="input-10-1"
          @click:append="show1 = !show1"
          :error="errors.hasOwnProperty('password')"
          :messages="errors.password"
          :disabled="loading"
        >
        </v-text-field>
      </div>

      <div>
        <button
          :class="{ loading: loading }"
          :disabled="loading"
          type="submit"
          class="btn btn-primary arrow"
        >
          Submit
        </button>
      </div>

      <div class="aa-login-divider">
        <span>OR</span>
      </div>

      <div class="aa-google-signin">
        <button
          @click="handleGoogleSignIn"
          :disabled="loading"
          type="button"
          class="btn btn-google"
        >
          <svg v-if="!loading" width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            <path fill="none" d="M0 0h48v48H0z"/>
          </svg>
          <v-progress-circular
            v-if="loading"
            indeterminate
            size="18"
            width="2"
            color="#4285F4"
          ></v-progress-circular>
          <span v-if="loading">Authenticating...</span>
          <span v-else>Sign in with Google</span>
        </button>
      </div>
    </v-form>
    <p class="aa-login-form__reset">
      Forgot password? <button @click="resetForm">Reset</button>
    </p>
  </v-card>
</template>

<script>
import { getError } from "@/utils/helpers";
import AuthService from "@/services/AuthService";
import CartService from "@/services/CartService";

export default {
  name: "LoginForm",
  methods: {
    async handleGoogleSignIn() {
      try {
        this.loading = true;
        this.$emit("formLoad", this.loading);

        // Load Google Identity Services
        await this.$googleAuth.load();

        // Initialize Google Sign-In with callback
        window.google.accounts.id.initialize({
          client_id: this.$googleAuth.clientId,
          callback: this.handleGoogleCallback
        });

        // Prompt the user to select an account
        window.google.accounts.id.prompt();

      } catch (error) {
        this.loading = false;
        this.$emit("formLoad", this.loading);
        console.error("Google sign-in error:", error);
        this.errors = { email: ["Failed to sign in with Google. Please try again."] };
      }
    },
    async handleGoogleCallback(response) {
      try {
        // Show loading state
        this.loading = true;
        this.$emit("formLoad", this.loading);
        
        // response.credential contains the JWT token
        const credential = response.credential;

        // Send JWT to backend
        const backendResponse = await AuthService.loginWithGoogle(credential);
        
        if (backendResponse.data.token) {
          // Store the token
          localStorage.setItem("auth_token", backendResponse.data.token);
          
          // Get user data
          const authUser = await this.$store.dispatch("auth/getAuthUser");
          
          if (authUser) {
            this.$store.dispatch("auth/setGuest", { value: "isNotGuest" });

            // Handle guest cart migration
            if (localStorage.guestCart) {
              const localCartData = JSON.parse(
                localStorage.getItem("guestCart") || "[]"
              );

              let localCartItems = localCartData.cart_items;

              for (const el of localCartItems) {
                let item = el;
                delete item["local_id"];
                delete item["id"];
                delete item["image"];
                await CartService.addCartItem(item);
              }

              localStorage.removeItem("guestCart");
            }

            localStorage.setItem("guest", "isNotGuest");
            await this.$store.dispatch("cart/getCart");

            !this.isFlagPath && this.$router.push({ path: "/my-account" });
          }

          this.$emit("upDataAuth");
        }

        this.loading = false;
        this.$emit("formLoad", this.loading);

      } catch (error) {
        this.loading = false;
        this.$emit("formLoad", this.loading);
        console.error("Google sign-in error:", error);
        this.errors = { email: ["Failed to sign in with Google. Please try again."] };
      }
    },
    resetForm() {
      const el = document.querySelector('[href="#forgot-password"]');
      el.click();

      const currentQuery = this.$route.query;
      if (currentQuery.p !== "forgot_password") {
        this.$router.push({ query: { p: "forgot_password" } });
      }
    },
    async submit() {
      if (this.$refs.form.validate()) {
        this.loading = true;
        this.$emit("formLoad", this.loading);
        const payload = {
          email: this.email,
          password: this.password,
        };
        this.error = null;
        try {
          await AuthService.login(payload);

          const authUser = await this.$store.dispatch("auth/getAuthUser");
          if (authUser) {
            this.$store.dispatch("auth/setGuest", { value: "isNotGuest" });

            if (localStorage.guestCart) {
              const localCartData = JSON.parse(
                localStorage.getItem("guestCart") || "[]"
              );

              let localCartItems = localCartData.cart_items;

              for (const el of localCartItems) {
                let item = el;

                delete item["local_id"];
                delete item["id"];
                delete item["image"];

                await CartService.addCartItem(item);
              }

              localStorage.removeItem("guestCart");
            }

            localStorage.setItem("guest", "isNotGuest");
            await this.$store.dispatch("cart/getCart");

            !this.isFlagPath && this.$router.push({ path: "/my-account" });
          } else {
            const error = Error(
              "Unable to fetch user after login, check your API settings."
            );
            error.name = "Fetch User";
            this.loading = false;
            this.$emit("formLoad", this.loading);
            throw error;
          }

          this.$emit("upDataAuth");
        } catch (error) {
          this.loading = false;
          this.$emit("formLoad", this.loading);
          console.log(this.loading);
          this.errors = getError(error);
        }
      }
    },
  },
  data: () => {
    return {
      tab: null,
      loading: false,
      password: "",
      email: null,
      show1: false,
      errors: {},
      requiredRules: [(v) => !!v || "This field is required."],
      loginEmailRules: [
        (v) => !!v || "This field is required.",
        (v) => /.+@.+\..+/.test(v) || "Email must be valid",
      ],
      emailRules: [
        (v) => !!v || "This field is required.",
        (v) => /.+@.+\..+/.test(v) || "Email must be valid",
      ],
      rules: {
        required: (value) => !!value || "This field is required.",
        min: (v) => (v && v.length >= 8) || "Min 8 characters",
      },
    };
  },
  props: ["someProps", "isFlagPath"],
  async mounted() {
    if (this.loading) this.doSomething();

    this.$store
      .dispatch("loader/showLoader", {
        loading: false,
      })
      .catch((error) => {
        console.log(error);
      });
  },
};
</script>

<style lang="scss">
@import "./login-style.scss";
</style>
